/**
 * useTranslationRunProgress.ts — PLAT-11690
 *
 * Smooth estimated progress for a translation run, the mirror of
 * `useDubGenerateProgress`. The translate endpoint exposes no percentage — it
 * is a single blocking request that resolves with the translation — so this
 * interpolates over elapsed time against an expected duration, ratcheted
 * monotonic and capped below 100, the same curve the dub and publish drawers
 * use.
 */

import { useEstimatedProgress } from '@src/components/common/useEstimatedProgress';
import { estimateTranslateMs } from '@src/config/processingEstimates';

/** Hard cap while translating — the real completion is signalled separately. */
export const TRANSLATE_PROGRESS_CAP = 95;
export const TRANSLATE_TICK_MS = 500;
// Same decay as the publish / dub curves: ~86% of the span reached at the
// expected ETA, still creeping toward the cap when a run outlasts the estimate.
const TRANSLATE_DECAY_RATE = 2;

export interface TranslateProgressInputs {
  transcriptElementCount?: number | null;
  compositionTargetCount?: number | null;
}

export interface TranslateProgressRun {
  /** Expected wall-clock of the run, computed once when it started. */
  etaMs: number;
  /** Epoch ms the run started — the estimate continues across a remount. */
  startedAt: number;
}

/**
 * Monotonic 0–100 progress while a translation is running.
 *
 * Pass the store's `TranslationRun` and the clock is anchored to when the
 * request actually started, so the chip and a reopened generate popover agree,
 * and a chip that unmounts with its tab continues instead of restarting at 0%.
 * Passing plain inputs instead anchors the clock to when `active` flipped on.
 */
export function useTranslationRunProgress(
  active: boolean,
  inputs: TranslateProgressInputs | TranslateProgressRun,
): number {
  const isRun = 'etaMs' in inputs;
  const etaMs = isRun ? inputs.etaMs : estimateTranslateMs(inputs);
  return useEstimatedProgress(active, etaMs, {
    cap: TRANSLATE_PROGRESS_CAP,
    tickMs: TRANSLATE_TICK_MS,
    decay: TRANSLATE_DECAY_RATE,
    startedAt: isRun ? inputs.startedAt : undefined,
  });
}
